import { FollowUpFormState, RootState } from '@/store/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import _ from 'lodash';
import * as NProgress from 'nprogress';
import { addFollowUpValidationSchema, clearErrors, validateSchema } from '@/utils/validation';
import axios from '@/utils/axios';

type AddFollowUpPayload = {
    description: { value: string };
    sysDocuments?: { value: string[] };
};

export const state: FollowUpFormState = {
    description: '',
    documents: [],
    isSaving: false,
    isUploading: false,
    errors: {},
};

export const getters: GetterTree<FollowUpFormState, RootState> = {
    getFieldErrors: state => (field: string) => state.errors[field],
    isFormValid: state => _.isEmpty(state.errors),
};

export const mutations: MutationTree<FollowUpFormState> = {
    setDescription(state, payload) {
        state.description = payload.description;
        state.errors = clearErrors(state.errors, 'description');
    },
    setDocuments(state, payload) {
        state.documents = payload.documents;
    },
    setIsSaving(state, payload) {
        state.isSaving = payload.isSaving;
        if (state.isSaving) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    },
    setIsUploading(state, payload) {
        state.isUploading = payload.isUploading;
    },
    resetForm(state) {
        state.description = '';
        state.documents = [];
        state.isSaving = false;
        state.isUploading = false;
    },
    setErrors(state, payload) {
        state.errors = payload.errors;
    },
};

export const actions: ActionTree<FollowUpFormState, RootState> = {
    async addFollowUp({ rootState, state, dispatch, commit }) {
        const errors = await validateSchema(addFollowUpValidationSchema, {
            description: state.description,
        });
        if (errors) {
            return commit('setErrors', { errors });
        }
        commit('setIsSaving', { isSaving: true });

        const payload: AddFollowUpPayload = {
            description: {
                value: state.description,
            },
        };

        const nonErroredDocuments = state.documents?.filter(d => !d.error).map(d => d.id);
        if (nonErroredDocuments?.length) {
            payload.sysDocuments = { value: nonErroredDocuments };
        }
        try {
            await axios.post(
                `${rootState.instanceId}/portal_data/grievances/item/${rootState.session?.recordId}/relations/followup_for_portal/add`,
                payload,
            );
            await dispatch('grievanceItem/fetchData', { noLoading: true, noReset: true }, { root: true });
        } finally {
            commit('setIsSaving', { isSaving: false });
        }
    },
};

const FollowUpFormStore: Module<FollowUpFormState, RootState> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default FollowUpFormStore;
